<template>
    <pendo-multiselect
        ref="multiselect"
        class="group-dropdown"
        :value="selectedOption"
        hide-selected-options
        :options="options"
        :loading="loadingDropdown"
        :multiple="multiple"
        full-width
        label-key="name"
        :extra-label-width="60"
        value-key="id"
        :disabled="disabled"
        @select="onGroupSelect"
        @open="onOpen"
        @close="onClose">
        <template #header="{ toggleMenu, model }">
            <div
                v-if="model"
                class="group-dropdown__header">
                <div
                    v-for="option in model"
                    :key="option.id"
                    class="group-dropdown__selected-option">
                    <pendo-group-option
                        :option="option"
                        @click="toggleMenu" />
                </div>
            </div>
        </template>
        <template #placeholder>
            <div
                v-if="isImportFeedback || !showUngroupedOption"
                class="placeholder-container">
                {{ $t('groupDropdown.Select option') }}
            </div>
            <div v-else>
                <pendo-group-option :option="ungroupedOption" />
            </div>
        </template>
        <template #tag="{ option }">
            <voc-item-multiselect-tag
                :option="option"
                :color="getProductAreaColor(option)"
                :is-open="isOpen"
                type="product-area"
                @remove-item="onGroupRemove" />
        </template>
        <template #selectedLabel="{ option }">
            <pendo-group-option :option="option" />
        </template>
        <template #option="{ option }">
            <pendo-group-option :option="option" />
        </template>
        <template #footer>
            <div
                v-if="showCreate && canCreate"
                class="group-dropdown__footer">
                <pendo-button
                    theme="app"
                    type="subtle"
                    size="small"
                    :label="$t('groupDropdown.Create Product Area')"
                    prefix-icon="plus"
                    icon-size="10"
                    @click="onGroupCreation" />
            </div>
        </template>
    </pendo-multiselect>
</template>

<script>
import VocItemMultiselectTag from '@/voc/components/voc-item-multiselect/voc-item-multiselect-tag/voc-item-multiselect-tag.vue';
import { PendoMultiselect, PendoGroupOption, PendoButton } from '@pendo/components';
import { mapState, mapGetters, mapActions } from 'vuex';
import { GROUP_COLORS } from '@/constants';

export default {
    name: 'GroupDropdown',
    components: {
        PendoMultiselect,
        PendoGroupOption,
        PendoButton,
        VocItemMultiselectTag,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        showCreate: {
            type: Boolean,
            default: false,
        },
        isIdeaGroup: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isImportFeedback: {
            type: Boolean,
            default: false,
        },
        showUngroupedOption: {
            type: Boolean,
            default: true,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input', 'remove', 'groupCreation'],
    data () {
        return {
            ungroupedOption: {},
            isOpen: false,
        };
    },
    computed: {
        ...mapState({
            isFetchingGroups: (state) => state.groups.fetching,
        }),
        ...mapGetters({
            groups: 'groups/list',
        }),
        canCreate () {
            return this.$can({ action: 'create', entity: 'groups' });
        },
        options () {
            if (this.isFetchingGroups && !this.isIdeaGroup) {
                return [];
            }

            const groupOptions = this.groups.map((group) => ({
                id: group.id,
                name: group.name,
                group,
            }));

            // Sort the groups by name here, and not using the prop in the component,
            // because it overrides the import feedback sort order.
            groupOptions.sort((a, b) => a.name.localeCompare(b.name));

            if (this.isImportFeedback) {
                return [this.ungroupedOption, ...groupOptions];
            }

            return this.showUngroupedOption ? [...groupOptions, this.ungroupedOption] : groupOptions;
        },
        selectedOption () {
            if (!this.value && this.isImportFeedback) {
                return null;
            }

            if (!this.value && this.showUngroupedOption) {
                return this.ungroupedOption;
            }

            if (this.multiple && this.isFetchingGroups) {
                return [];
            }

            return this.multiple
                ? this.value.map((selectedValue) => this.options.find((opt) => opt.id === selectedValue.id))
                : this.options.find((option) => option.id === this.value.id);
        },
        loadingDropdown () {
            if (this.isIdeaGroup) return this.loading;

            return this.isFetchingGroups;
        },
        getProductAreaColor () {
            return (option) => GROUP_COLORS.colorMap[option.group.color] || null;
        },
    },
    created () {
        this.ungroupedOption = {
            id: '',
            name: this.$t('No Product Area'),
            group: {
                color: '.groupColorUngrouped',
                name: 'No Product Area',
                id: '',
            },
        };
        this.fetchGroups();
    },
    methods: {
        ...mapActions({
            fetchGroups: 'groups/loadAll',
        }),
        onGroupSelect (option) {
            this.$emit('input', option.group);
        },
        onGroupCreation () {
            this.$refs.multiselect.toggleMenu();
            this.$emit('groupCreation');
        },
        onGroupRemove (option) {
            this.$emit('remove', option.group);
        },
        onOpen () {
            this.isOpen = true;
        },
        onClose () {
            this.isOpen = false;
        },
    },
};
</script>

<style lang="less" scoped>
.group-dropdown__header {
    padding: 8px 0;
    max-height: 100px;
    overflow-y: scroll;
    border-bottom: 1px solid var(--palette-gray-30);
    display: flex;
    flex-direction: column;
    width: 100%;

    .group-dropdown__selected-option {
        padding: 0 16px;
        height: 26px;
        flex: 1;
        cursor: pointer;

        &:hover {
            background-color: var(--palette-gray-20);
        }
    }
}

.group-dropdown__footer {
    display: flex;
    align-items: center;
    border-top: 1px solid var(--palette-gray-30);
    padding: 0 8px;
}
</style>
